import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Building2 } from 'lucide-react';
import { SectionCard } from '@reflex/ui/components/shared/SectionCard';
import { Button } from '@reflex/ui/components/ui/button';
import { Spinner } from '@reflex/ui/components/shared/Spinner';
import { useAuth } from '@/hooks/auth/useAuth';
import { useActiveOrgSlug } from '@/lib/useOrgRoutes';
import { connectProviderPopup } from '@/lib/auth-provider-connect';
import { showProviderConnectionError } from '@/lib/auth-provider-error';
import { queryKeys } from '@/lib/query-keys';

const PROVIDER = 'oidc';
const PROVIDER_LABEL = 'Company SSO';

/**
 * Offers a signed-in member a way to attach their organization's SSO
 * identity to the account they're already using, ahead of an admin turning
 * on required SSO (`plugin-auth-oidc`'s link start route,
 * `POST /auth/oidc/realm-link-start?org=<slug>`).
 *
 * Whether the org actually has a usable connection can only be read via
 * `GET /organizations/:id/idp-config` (idp-config.routes.ts) and
 * `GET /enterprise-identity/oidc-connection` (billing-entity-oidc.routes.ts)
 * — both deliberately `company:read`-gated (Owner, Admin, Company Manager),
 * excluded from the derived read set precisely so an ordinary member cannot
 * use them to probe the company's identity setup. That is also exactly who
 * this card is for: PR #4351's defect #1 is a member with a password
 * account and no way to pick up the company identity. Gating the button on
 * either admin-only read would hide it from the people it exists for, so
 * the button is offered unconditionally to any signed-in member with an
 * active org and lets the start route be the sole authority. There is no
 * enumeration risk in doing so: the caller is already an authenticated
 * member of the org they'd be linking against, and the route answers every
 * "cannot" case (SSO off, no connection, not entitled, unreachable IdP)
 * with the same generic `sso_unavailable`, which `handleLink` turns into an
 * actionable-but-honest toast instead of a silent bounce.
 *
 * The sign-in screen's `SignInWithSso` already follows this pattern: it
 * offers "Sign in with company SSO" unconditionally too, with no
 * availability check ahead of the (unauthenticated) start route.
 *
 * Once linked, `auth_identities` already carries an `oidc` row, so this
 * card steps aside and the standard provider list (which keys visibility
 * off identity presence, not the operator's hide flag) takes over as the
 * single place to view or unlink it.
 */
export function CompanySsoCard({ alreadyLinked }: { alreadyLinked: boolean }) {
  const { activeOrganizationId } = useAuth();
  const orgSlug = useActiveOrgSlug();
  const queryClient = useQueryClient();
  const [linking, setLinking] = useState(false);

  async function handleLink() {
    setLinking(true);
    try {
      const result = await connectProviderPopup(
        PROVIDER,
        'link',
        `/auth/oidc/realm-link-start?org=${encodeURIComponent(orgSlug)}`,
      );
      if (result?.error) {
        showProviderConnectionError({
          provider: PROVIDER,
          providerLabel: PROVIDER_LABEL,
          error: result.error,
          onRetry: () => void handleLink(),
        });
      } else if (result?.context?.linked === '1') {
        toast.success(`${PROVIDER_LABEL} connected`);
      }
    } finally {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.userIdentities }),
        queryClient.invalidateQueries({ queryKey: queryKeys.userProviders }),
      ]);
      setLinking(false);
    }
  }

  // Not "does the org have SSO" (unreadable at member tier) — just "is
  // there an org to link against at all".
  if (alreadyLinked || !activeOrganizationId) return null;

  return (
    <SectionCard
      title="Company single sign-on"
      description="Attach your organization's identity provider to this account. You'll keep signing in the same way until an admin requires SSO."
      icon={<Building2 />}
      data-testid="company-sso-card"
    >
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => void handleLink()}
        disabled={linking}
      >
        {linking ? <Spinner className="h-4 w-4" /> : 'Link company SSO'}
      </Button>
    </SectionCard>
  );
}
